import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  makeStyles,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@material-ui/core";
import { Delete, Edit } from "@material-ui/icons";
import dayjs from "dayjs";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useTable,
} from "react-table";
import { toast } from "react-toastify";
import { CustomCheckbox, modal } from "../../../../../util";
import apiHelper from "../../../../../util/ApiHelper";
import { useLoading } from "../../../../../util/LoadingProvider";
import { PaginationHandler } from "../../../../../util/useManualPagination";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import PaginationBarForHandler from "../../../../common/PaginationBarForHandler";

const useStyles = makeStyles((theme) => ({
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "200px",
    },
  },
  text: {
    textDecoration: "none",
    color: "#306F9D",
  },
  pagination: {
    "& .MuiTextField-root": {
      width: "35px",
      minWidth: "auto",
    },
    "& .MuiSelect-root": {
      paddingTop: "3px",
      paddingBottom: "3px",
    },
    "& input": {
      padding: "4px 0",
      marginLeft: "10px",
      marginRight: "10px",
    },
    "& button": {
      background: "white",
      color: "#cccccc",
      border: "1px solid #CCCCCC",
      minWidth: "auto",
      width: "24px",
      height: "24px",
      margin: "4px 0",
      "&.left": {
        borderRadius: "4px 0 0 4px",
        marginLeft: "10px",
      },
      "&.right": {
        borderRadius: " 0 4px 4px 0",
      },
    },
  },
}));

/** DRAG N DROP PREPARATION STARTS **/

const IsDraggingContext = React.createContext(false);

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

class DraggableTableCell extends React.Component {
  getSnapshotBeforeUpdate(prevProps) {
    if (!this.ref) {
      return null;
    }
    const isDragStarting =
      this.props.isDragOccurring && !prevProps.isDragOccurring;
    if (!isDragStarting) {
      return null;
    }
    const { width, height } = this.ref.getBoundingClientRect();
    const snapshot = {
      width,
      height,
    };
    return snapshot;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const ref = this.ref;
    if (!ref) {
      return;
    }
    if (snapshot) {
      if (ref.style.width === snapshot.width) {
        return;
      }
      ref.style.width = `${snapshot.width}px`;
      ref.style.height = `${snapshot.height}px`;
      return;
    }
    if (this.props.isDragOccurring) {
      return;
    }
    // inline styles not applied
    if (ref.style.width == null) {
      return;
    }
    // no snapshot and drag is finished - clear the inline styles
    ref.style.removeProperty("height");
    ref.style.removeProperty("width");
  }

  setRef = (ref) => {
    this.ref = ref;
  };

  render() {
    return (
      <TableCell
        style={{ paddingTop: 6, paddingBottom: 6 }}
        ref={this.setRef}
        align={this.props.align}
      >
        {this.props.children}
      </TableCell>
    );
  }
}

const DraggableTableRow = ({ snapshot, data, row, provided, className }) => {
  return (
    <IsDraggingContext.Consumer>
      {(isDragging) => (
        <TableRow
          className={className}
          ref={provided.innerRef}
          isDragging={snapshot.isDragging}
          {...provided.draggableProps}
        >
          {row.cells.map((cell) => {
            if (cell.column.id !== "dragHandle") {
              return (
                <DraggableTableCell isDragOccurring={isDragging} scope="row">
                  {cell.render("Cell")}
                </DraggableTableCell>
              );
            } else {
              return null;
            }
          })}
          <TableCell
            style={{ paddingTop: 6, paddingBottom: 6, color: "#CCCCCC" }}
            {...provided.dragHandleProps}
          >
            <DragHandleIcon />
          </TableCell>
        </TableRow>
      )}
    </IsDraggingContext.Consumer>
  );
};

/** DRAG N DROP PREPARATION ENDS **/


const statuses = Object.freeze({
  ACTIVE: "active",
  INACTIVE: "inactive",
});

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return <CustomCheckbox type="checkbox" ref={resolvedRef} {...rest} />;
  }
);

const formatDatetime = (dt) => {
  return dayjs(dt).format("DD MMM YYYY, hh:mma");
};

export default function AwardCategoryTable({ match, disabled }) {
  const classes = useStyles();
  const [data, setData] = React.useState([]);
  const [totalPages, setTotalPages] = React.useState(1);
  const [totalElements, setTotalElements] = React.useState(0);
  const [keyword, setKeyword] = React.useState("");
  const { setLoading } = useLoading();

  // for pagination
  const [paginationHandler] = React.useState(new PaginationHandler());
  const [allCounts, setAllCounts] = React.useState(0);
  const [inactiveCounts, setInactiveCounts] = React.useState(0);
  const [displayAll, setDisplayAll] = React.useState(false);

  // for Drag & Drop
  const [orderingDisabled, setOrderingDisabled] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const onBeforeDragStart = () => {
    setIsDragging(true);
  };
  const onDragEnd = (result) => {
    //callback passed to dragdrop context
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    const items = reorder(data, result.source.index, result.destination.index);
    console.log("ON DRAG END:", items);
    setData(items);
  };

  const CustomLink = React.useMemo(
    () =>
      React.forwardRef((linkProps, ref) => <Link ref={ref} {...linkProps} />),
    []
  );

  const columns = React.useMemo(
    () => [
      {
        Header: "Title En",
        accessor: "localizations.en.name",
        Cell: ({ row }) => {
          return (
            <Link
              to={`${match.url}/${row.original.id}`}
              className={classes.text}
            >
              {row.original.localizations &&
                row.original.localizations.en &&
                row.original.localizations.en.name}
            </Link>
          );
        },
      },
      {
        Header: "Title Tc",
        accessor: "localizations.tc.name",
        disableGlobalFilter: true,
        Cell: ({ row }) => {
          return (
            <Link
              to={`${match.url}/${row.original.id}`}
              className={classes.text}
            >
              {row.original.localizations &&
                row.original.localizations.tc &&
                row.original.localizations.tc.name}
            </Link>
          );
        },
      },
      {
        Header: "Status",
        accessor: "status",
        disableGlobalFilter: true,
        Cell: ({ row }) => {
          return (
            <span className={classes.text}>
              {row.original.status ? statuses.ACTIVE : statuses.INACTIVE}
            </span>
          );
        },
      },
      {
        Header: "Last Update",
        accessor: "updateTime",
        disableGlobalFilter: true,
        Cell: ({ row }) => {
          return formatDatetime(row.original.updateTime);
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const {
    getTableProps,
    headerGroups,
    rows,
    prepareRow,
    setPageSize,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    selectedFlatRows,
    setGlobalFilter,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
      manualPagination: true,
      pageCount: totalPages,
    },
    useGlobalFilter,
    usePagination,
    useRowSelect,
    (hooks) => {
      hooks.visibleColumns.push((columns) => [
        ...columns,
        {
          id: "action",
          // The header can use the table's getToggleAllRowsSelectedProps method
          // to render a checkbox
          Header: () => <div>Action</div>,
          // The cell can use the individual row's getToggleRowSelectedProps method
          // to the render a checkbox
          Cell: ({ row }) => (
            <div style={{ display: "inline-flex" }}>
              <IconButton
                size="small"
                component={CustomLink}
                to={`${match.url}/${row.original.id}`}
              >
                <Edit fontSize="small" />
              </IconButton>
              {disabled || (
                <IconButton
                  size="small"
                  onClick={() => {
                    deleteCategory(row.original.id);
                  }}
                >
                  <Delete fontSize="inherit" />
                </IconButton>
              )}
            </div>
          ),
        },
        // The drag handle can be used to drag the table's rows individually
        {
          id: "dragHandle",
          Header: () => <></>,
        },
      ]);
    }
  );

  const getAwardCategory = React.useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiHelper.hkiff.getAwardCategory(
        null,
        pageIndex,
        (pageIndex + 1) * pageSize,
        pageSize,
        keyword,
        null,
        true
      );
      paginationHandler.setPageIndex(0).setRows(result.data.content);
      const content = paginationHandler.filterRowsByAll().getRows();
      const totalPages = paginationHandler.getTotalPages();
      const all = paginationHandler.getStatuslessRows().length;
      setData(content.filter((x) => x));
      setTotalPages(totalPages);
      setTotalElements(all)
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [setLoading, keyword]);

  const triggerSearch = React.useCallback(() => {
    setGlobalFilter(keyword);
  }, [keyword, setGlobalFilter]);

  const deleteCategory = React.useCallback(
    async (id) => {
      const decision = await modal.fire({
        title: "Delete Category",
        text: "Are you sure to delete this category?",
        showCancelButton: true,
        confirmButtonText: `Delete`,
      });
      if (decision.isConfirmed) {
        try {
          const result = await apiHelper.hkiff.deleteAwardCategoryById(id);
          if (result.status === 200) {
            toast.dark("category deleted.");
            await getAwardCategory();
          }
        } catch (err) {
          console.log(err);
        }
      }
    },
    [getAwardCategory]
  );

  const saveOrdering = async () => {
    try {
        const seqData = {};
        data.forEach((e, i) => {
            seqData[e.id] = 1 + i;
        });
        const result = await apiHelper.hkiff.orderAwardCategory(seqData);
        if (result.data.code === 0) {
            toast.dark("Your changes is saved.");
            await getAwardCategory();
        }
    } catch (err) {
        console.log(err);
    }
  };

  // disable ordering if search
  React.useEffect(() => {
    if (keyword || displayAll == true) {
      setOrderingDisabled(true);
    } else {
      setOrderingDisabled(false);
    }
  }, [keyword, displayAll]);

  React.useEffect(() => {
    if (paginationHandler) {
      getAwardCategory(); // only get sponsors if paginationHandler ready
    }
  }, [getAwardCategory, paginationHandler]);


  return (
    <>
      <Grid container justify="space-between" style={{ paddingBottom: "10px" }}>
        <Grid container item xs={12} md={12}>
          <Box
            width="100%"
            component="span"
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Box display="flex">
              <TextField
                id="outlined-error"
                label="Search"
                variant="outlined"
                margin="dense"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
            </Box>
            <Box display="flex">
              <Button
                variant="outlined"
                color="primary"
                type="button"
                margin="dense"
                onClick={triggerSearch}
              >
                Search
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <IsDraggingContext.Provider value={isDragging}>
      <TableContainer
        component={Paper}
        style={{
          boxShadow: "none",
          border: "1px solid #cccccc",
        }}
      >
        <fieldset disabled={disabled} className="fieldset">
          <Table
            aria-label="simple table"
            component="ul"
            style={{ margin: "0" }}
            {...getTableProps()}
          >
            <colgroup>
              <col style={{ width: "40%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
            </colgroup>
            <TableHead component="div">
              {headerGroups.map((headerGroup) => (
                <TableRow {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <TableCell
                      {...column.getHeaderProps()}
                      className={`${column.className ?? ""}`}
                      style={{
                        paddingTop: 6,
                        paddingBottom: 6,
                        lineHeight: "1.1em",
                        height: "48px",
                      }}
                    >
                      <span
                        className={
                          column.disableSortBy !== true
                            ? classes.sortableHeader
                            : ``
                        }
                      >
                        {column.render("Header")}
                      </span>
                      {column.id !== "selection" &&
                        column.id !== "dragHandle" &&
                        column.disableSortBy !== true ? (
                          <TableSortLabel
                            active={column.isSorted}
                            // react-table has an unsorted state which is not treated here
                            direction={column.isSortedDesc ? "desc" : "asc"}
                          />
                        ) : null}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <DragDropContext
              onBeforeDragStart={onBeforeDragStart}
              onDragEnd={onDragEnd}
            >
              <Droppable droppableId="droppable">
                {(provided, snapshot) => (
                  <TableBody
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                  >
                    {rows.map((row, index) => {
                      prepareRow(row);
                      return (
                        <Draggable
                          key={row.id}
                          draggableId={row.id}
                          index={index}
                          isDragDisabled={disabled || orderingDisabled}
                        >
                          {(provided, snapshot) => (
                            <DraggableTableRow
                              key={row.id}
                              data={row.original}
                              row={row}
                              provided={provided}
                              snapshot={snapshot}
                            />
                          )}
                        </Draggable>
                      );
                    })}
                  </TableBody>
                )}
              </Droppable>
            </DragDropContext>
          </Table>
        </fieldset>
      </TableContainer>
      </IsDraggingContext.Provider>
      <Grid container justify="space-between">
          <Grid container item xs={4}>
            <Box display="flex">
              <Button
                variant="contained"
                color="primary"
                type="button"
                disabled={disabled || orderingDisabled}
                onClick={saveOrdering}
              >
                Save Changes
              </Button>
            </Box>
          </Grid>
      <Grid
        container
        justify="flex-end"
        style={{
          paddingRight: "25px",
          marginTop: "15px",
        }}
      >
        <div>
          <div></div>
          <PaginationBarForHandler
            pageSize={pageSize}
            setPageSize={setPageSize}
            setData={setData}
            setTotalPages={setTotalPages}
            paginationHandler={paginationHandler}
            gotoPage={gotoPage}
            pageIndex={pageIndex}
            canPreviousPage={canPreviousPage}
            previousPage={previousPage}
            nextPage={nextPage}
            canNextPage={canNextPage}
            elementCounts={{all: totalElements, active:0, inactive: 0}}
            status={'all'}
          />
        </div>
      </Grid>
      </Grid>
    </>
  );
}
